// Section (Patterns; was primitives.tsx's, HIERARCHY follow-up 9, BL-189): a ruled section of a page
// (DESIGN.md §3a): a rule above, its name under it with its count, muted, beside it, then what's in it.
// Never a box. One after another they keep a gap; the first in its column takes none. Area is the one
// that folds; a Section never does. It keeps base.css's classes (.flw-sec), which page sheets place by.
// Where what it holds stands (BL-189, a sales tax return's Not filed) is StateMark and its word at the
// end of the name's line: in ink while it waits, muted once settled. ListPage's regions and SettingsPage's
// sections are Sections (BL-189): named by their heading (headingId), the count with it, and a section's
// one action beside its name when it has no line of its own (Tax zones' Add a tax zone). Its name is an h2
// on a page, and an h3 in a sheet whose own title is the h2 (the invoice sheet), so the levels never jump
// or repeat.
// A section says its own state (BL-189: ListPage's regions, DashboardPage's sections, SettingsPage's
// settings), so every page says it the same way: loading draws skeleton rows, empty one muted sentence,
// failed one red sentence and Try again. No load waits forever: still loading after 10 s it says so
// under the rows ("Still loading the equipment…"), and after 30 s it gives up into the red sentence with
// Try again ("Couldn’t load the equipment."), which starts the wait again. A pattern that places the
// words itself (SettingsSection, under its line; DashboardPage's whole body) draws SectionSays.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link } from '../../components/Link/Link.tsx';
import { Skeleton, SkeletonGroup } from '../../components/Skeleton/Skeleton.tsx';
import { StateMark, type MarkState } from '../../components/StateMark/StateMark.tsx';
import './Section.css';

export type ListStatus = 'ready' | 'loading' | 'empty' | 'failed' | 'slow' | 'timedout';

/** A section still loading says so after this long (RecordPage's slow), */
export const LIST_SLOW_MS = 10_000;
/** and gives up after this long, into Couldn’t load and Try again. */
export const LIST_GIVE_UP_MS = 30_000;

const WaitTimes = createContext({ slowAfter: LIST_SLOW_MS, giveUpAfter: LIST_GIVE_UP_MS });

/** Shorter waits for every section inside (a page story's Slow and Timed out, a test). */
export function ListWaitTimes({
  slowAfter = LIST_SLOW_MS,
  giveUpAfter = LIST_GIVE_UP_MS,
  children,
}: {
  slowAfter?: number;
  giveUpAfter?: number;
  children: ReactNode;
}) {
  return <WaitTimes.Provider value={{ slowAfter, giveUpAfter }}>{children}</WaitTimes.Provider>;
}

/** What a section that is still loading says, `ms` into its wait. */
export function listWaitAt(
  ms: number,
  slowAfter = LIST_SLOW_MS,
  giveUpAfter = LIST_GIVE_UP_MS,
): 'loading' | 'slow' | 'timedout' {
  return ms >= giveUpAfter ? 'timedout' : ms >= slowAfter ? 'slow' : 'loading';
}

/** A loading section's wait: loading, then slow, then timed out; again starts it over (Try again). */
function useWait(on: boolean, slowAfter: number, giveUpAfter: number) {
  const first = () => listWaitAt(0, slowAfter, giveUpAfter);
  const [wait, setWait] = useState(first);
  const [round, setRound] = useState(0);
  // a new load (loading again after an answer) starts its wait over
  const [wasOn, setWasOn] = useState(on);
  if (on !== wasOn) {
    setWasOn(on);
    setWait(first());
  }
  useEffect(() => {
    if (!on) return;
    const t = [slowAfter, giveUpAfter].map((ms) =>
      window.setTimeout(() => setWait(listWaitAt(ms, slowAfter, giveUpAfter)), ms),
    );
    return () => t.forEach((x) => window.clearTimeout(x));
  }, [on, round, slowAfter, giveUpAfter]);
  const again = () => {
    setWait(first());
    setRound((n) => n + 1);
  };
  return { wait, again };
}

/**
 * A loading section's wait: loading, then slow, then timed out, ListWaitTimes' waits unless given;
 * `again` (Try again) starts it over.
 */
export function useListWait(on: boolean, slowAfter?: number, giveUpAfter?: number) {
  const times = useContext(WaitTimes);
  return useWait(on, slowAfter ?? times.slowAfter, giveUpAfter ?? times.giveUpAfter);
}

/** What a section says while it isn't ready. */
export interface SectionWait {
  /** Ready (the default), loading, empty or failed; a long loading turns slow, then timed out. Slow and
   * timed out can be given too (a story, a page that knows). */
  status?: ListStatus;
  /** What it holds, for the words it says ("the equipment", "what’s due"); its title by default. */
  what?: string;
  /** Empty: what isn't there, in a sentence ("No service is due in the next 30 days."). */
  emptyText?: string;
  /** Failed: what didn't load ("The equipment didn't load."). */
  failedText?: string;
  onRetry?: () => void;
  /** Loading: what's coming, for screen readers ("Loading the equipment"). */
  loadingText?: string;
  /** How many skeleton rows to draw while loading (3). */
  loadingRows?: number;
  /** How long a load waits before it says it's slow, and before it gives up (10 s and 30 s, or
   * ListWaitTimes'). */
  slowAfter?: number;
  giveUpAfter?: number;
}

/** The words a section says while it isn't ready, for a pattern that places them itself. */
export function SectionSays({
  status,
  what,
  emptyText = 'Nothing here yet.',
  failedText = 'This didn’t load.',
  onRetry,
  loadingText,
  loadingRows = 3,
  slowAfter,
  giveUpAfter,
}: SectionWait & { status: Exclude<ListStatus, 'ready'>; what: string }) {
  const { wait, again } = useListWait(status === 'loading', slowAfter, giveUpAfter);
  const now = status === 'loading' ? wait : status;
  const rows = (label: string) => (
    <SkeletonGroup label={label}>
      {Array.from({ length: loadingRows }, (_, i) => (
        <Skeleton key={i} shape="row" width="full" />
      ))}
    </SkeletonGroup>
  );
  if (now === 'loading') return rows(loadingText ?? `Loading ${what}`);
  if (now === 'slow')
    return (
      <>
        {rows(`Still loading ${what}`)}
        <p className="flw-sec__say" data-slow="" role="status">
          {`Still loading ${what}…`}
        </p>
      </>
    );
  if (now === 'timedout')
    return (
      <p className="flw-sec__say" data-bad="" role="alert">
        <span>{`Couldn’t load ${what}.`}</span>
        <Link
          onPress={() => {
            again();
            onRetry?.();
          }}
        >
          Try again
        </Link>
      </p>
    );
  if (now === 'empty') return <p className="flw-sec__say">{emptyText}</p>;
  return (
    <p className="flw-sec__say" data-bad="" role="alert">
      <span>{failedText}</span>
      {onRetry ? <Link onPress={onRetry}>Try again</Link> : null}
    </p>
  );
}

export interface SectionProps extends SectionWait {
  /** Its name, under the rule ("Company holidays"); its name for screen readers too. */
  title: string;
  /** How many there are, beside the name, once it's ready: a number, or "20+" when only some are loaded
   * (ListPage's `more`). */
  count?: number | string;
  /** Where it stands, at the end of the name's line: StateMark's mark and its word ("Not filed"). */
  state?: { mark: MarkState; word: string };
  /** Its heading's id: the section is then named by its heading, the count with it (a page's region). */
  headingId?: string;
  /** Beside the name, at the end of its line: at most one Button (Add a tax zone). */
  action?: ReactNode;
  /** The name's heading level: 2 on a page, 3 in a sheet whose title is the h2. */
  level?: 2 | 3;
  /** What's in it, when it's ready. */
  children?: ReactNode;
}

export function Section({
  title,
  count,
  state,
  headingId,
  action,
  level = 2,
  children,
  ...wait
}: SectionProps) {
  const status = wait.status ?? 'ready';
  const H = level === 3 ? 'h3' : 'h2';
  const heading = (
    <H id={headingId}>
      {title}
      {status === 'ready' && count != null && <span className="flw-cnt">{count}</span>}
      {state && (
        <span className="flw-sec__state" data-state={state.mark}>
          <StateMark state={state.mark} />
          <span className="flw-sec__word">{state.word}</span>
        </span>
      )}
    </H>
  );
  return (
    <section
      className="flw-sec"
      {...(headingId ? { 'aria-labelledby': headingId } : { 'aria-label': title })}
    >
      {action ? (
        <div className="flw-sec__head">
          {heading}
          {action}
        </div>
      ) : (
        heading
      )}
      {status === 'ready' ? (
        children
      ) : (
        <SectionSays {...wait} status={status} what={wait.what ?? title.toLowerCase()} />
      )}
    </section>
  );
}
